/**
 * The GOOD BUILD OS primitives.
 *
 * These replace the third-party component library the app used to sit on. A themed library gets
 * you a competent dark app; it does not get you the board in `brand/`, where lemon is reserved
 * for one job, labels are set in mono, and the display face is only ever the studio's own voice.
 * So the primitives are ours, and they are small: a button, a surface, three kinds of type, a
 * label, a notice, an avatar and a spinner.
 *
 * The prop shapes are deliberately conventional — numbered sizes, named colours, a `render`
 * escape hatch — so a screen reads the same as it did before.
 */

import * as React from 'react';

/* ------------------------------------------------------------------- shared */

export type Size = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';

/**
 * The semantic colours a screen asks for, mapped onto the six brand colours.
 *
 * There is no violet and no red in the palette, so the two that used to be violet and red
 * become sand and the warm danger tone rather than being invented.
 */
export type Tone = 'gray' | 'green' | 'amber' | 'danger' | 'violet' | 'lemon' | 'jade' | 'sand';

const TEXT_TONE: Record<Tone, string> = {
  gray: 'gb-text--muted',
  green: 'gb-text--jade',
  jade: 'gb-text--jade',
  amber: 'gb-text--lemon',
  lemon: 'gb-text--lemon',
  danger: 'gb-text--danger',
  violet: 'gb-text--sand',
  sand: 'gb-text--sand',
};

const BADGE_TONE: Record<Tone, string> = {
  gray: 'gb-badge--fog',
  green: 'gb-badge--jade',
  jade: 'gb-badge--jade',
  amber: 'gb-badge--lemon',
  lemon: 'gb-badge--lemon',
  danger: 'gb-badge--danger',
  violet: 'gb-badge--sand',
  sand: 'gb-badge--sand',
};

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Renders `children` into the element given as `render`, keeping that element's own props and
 * adding ours. It is how a `Text` becomes a paragraph without a second component.
 */
function renderAs(
  element: React.ReactElement | undefined,
  className: string,
  props: Record<string, unknown>,
  children: React.ReactNode,
): React.ReactElement {
  if (element) {
    const own = element.props as { className?: string; children?: React.ReactNode };
    return React.cloneElement(element, {
      ...props,
      className: cx(className, own.className),
      children: own.children ?? children,
    } as never);
  }
  return (
    <span className={className} {...props}>
      {children}
    </span>
  );
}

/* --------------------------------------------------------------------- type */

export interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  size?: Size;
  color?: Tone;
  weight?: 'regular' | 'medium' | 'bold';
  /** Mono, for anything the console states as fact: a build number, a size, a time. */
  mono?: boolean;
  /** Render into this element instead of a span, keeping its own props. */
  render?: React.ReactElement;
  children?: React.ReactNode;
}

/** Body copy. Inter, never larger than a heading, never used to shout. */
export function Text({
  size = '2',
  color,
  weight,
  mono = false,
  render,
  className,
  children,
  ...rest
}: TextProps) {
  // Sizes above 5 are heading territory; clamp rather than inventing another step.
  const step = Math.min(Number(size), 5);
  return renderAs(
    render,
    cx(
      'gb-text',
      `gb-text--${step}`,
      color && TEXT_TONE[color],
      weight === 'bold' && 'gb-text--bold',
      weight === 'medium' && 'gb-text--medium',
      mono && 'gb-text--mono',
      className,
    ),
    rest,
    children,
  );
}

export interface HeadingProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  size?: Size;
  color?: Tone;
  /** Uppercase, the way a game title is set on the board. */
  caps?: boolean;
  render?: React.ReactElement;
  children?: React.ReactNode;
}

/** Nine sizes down to one of seven display steps, largest first. */
const HEADING_STEP: Record<string, number> = {
  '9': 1,
  '8': 2,
  '7': 3,
  '6': 4,
  '5': 5,
  '4': 6,
  '3': 7,
  '2': 7,
  '1': 7,
};

/** Sora. The studio's own voice: a title, a screen heading, a statement. Never a paragraph. */
export function Heading({
  size = '5',
  color,
  caps = false,
  render,
  className,
  children,
  ...rest
}: HeadingProps) {
  const step = HEADING_STEP[size] ?? 5;
  const element = render ?? <h2 />;
  return renderAs(
    element,
    cx(
      'gb-display',
      `gb-display--${step}`,
      color && TEXT_TONE[color],
      caps && 'gb-display--caps',
      className,
    ),
    rest,
    children,
  );
}

export interface EyebrowProps extends React.HTMLAttributes<HTMLElement> {
  tone?: 'sand' | 'lemon' | 'quiet';
  small?: boolean;
  children?: React.ReactNode;
}

/**
 * The mono label above a thing. `FEATURED`, `GAMES`, `BUILD 0.1.7`.
 *
 * It is the single most recognisable piece of the brand after the stripe, and it is the reason
 * a screen reads as a workshop rather than as a storefront.
 */
export function Eyebrow({
  tone = 'sand',
  small = false,
  className,
  children,
  ...rest
}: EyebrowProps) {
  return (
    <span
      className={cx(
        'gb-eyebrow',
        tone === 'lemon' && 'gb-eyebrow--lemon',
        tone === 'quiet' && 'gb-eyebrow--quiet',
        small && 'gb-eyebrow--small',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ button */

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  size?: Size;
  /** solid is lemon and there is one per screen; surface frames; ghost is quiet text. */
  variant?: 'solid' | 'surface' | 'ghost';
  color?: Tone;
  /** Fills the width of its container, for a form or a card footer. */
  block?: boolean;
  children?: React.ReactNode;
}

export function Button({
  size = '2',
  variant = 'solid',
  color,
  block = false,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const step = Math.min(Math.max(Number(size), 1), 4);
  return (
    <button
      type={type}
      className={cx(
        'gb-btn',
        `gb-btn--${step}`,
        `gb-btn--${variant}`,
        color === 'danger' && 'gb-btn--danger',
        block && 'gb-btn--block',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------- card */

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: Size;
  variant?: 'surface' | 'ghost' | 'solid' | 'glass';
  children?: React.ReactNode;
}

/** A framed charcoal panel. Anything important that sits over artwork gets one. */
export function Card({ size = '2', variant = 'surface', className, children, ...rest }: CardProps) {
  const step = Math.min(Math.max(Number(size), 1), 4);
  return (
    <div
      className={cx(
        'gb-card',
        `gb-card--${step}`,
        variant !== 'surface' && `gb-card--${variant}`,
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------- badge */

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  color?: Tone;
  solid?: boolean;
  children?: React.ReactNode;
}

export function Badge({ color = 'gray', solid = false, className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cx('gb-badge', BADGE_TONE[color], solid && 'gb-badge--solid', className)}
      {...rest}
    >
      {children}
    </span>
  );
}

/* ----------------------------------------------------------------- callout */

export interface CalloutRootProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  color?: Tone;
  children?: React.ReactNode;
}

function CalloutRoot({ color = 'jade', className, children, ...rest }: CalloutRootProps) {
  const tone =
    color === 'danger' ? 'danger' : color === 'amber' || color === 'lemon' ? 'amber' : null;
  return (
    <div
      role="status"
      className={cx('gb-callout', tone && `gb-callout--${tone}`, className)}
      {...rest}
    >
      {children}
    </div>
  );
}

function CalloutDescription({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...rest}>
      {children}
    </div>
  );
}

/** A short notice in place: something is happening, or something went wrong. */
export const Callout = { Root: CalloutRoot, Description: CalloutDescription };

/* ------------------------------------------------------------------ avatar */

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: Size;
  src?: string;
  /** Shown when there is no picture, or the picture fails to load. */
  fallback?: React.ReactNode;
  alt?: string;
}

export function Avatar({ size = '2', src, fallback, alt = '', className, ...rest }: AvatarProps) {
  const [failed, setFailed] = React.useState(false);
  const step = Math.min(Math.max(Number(size), 1), 4);

  return (
    <span className={cx('gb-avatar', step !== 2 && `gb-avatar--${step}`, className)} {...rest}>
      {src && !failed ? (
        <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        fallback
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ switch */

export interface SwitchProps {
  on: boolean;
  /** The word beside the switch. Colour is never the only thing that says which way it is. */
  onLabel?: string;
  offLabel?: string;
  className?: string;
}

/**
 * A setting that is on or off.
 *
 * Deliberately not lemon: lemon is the brand and the focus ring, and a column of settings all
 * wearing it turns the whole screen yellow (field rule 02). On is jade, which is the palette's
 * colour for live, and the word beside it carries the state on its own.
 *
 * It renders as a span rather than a control, because every interactive thing in this app is
 * activated through the focus wrapper that contains it.
 */
export function Switch({ on, onLabel = 'On', offLabel = 'Off', className }: SwitchProps) {
  return (
    <span className={cx('gb-switch', className)} data-on={on}>
      <span className="gb-switch__track" aria-hidden="true">
        <span className="gb-switch__knob" />
      </span>
      <span className="gb-switch__label">{on ? onLabel : offLabel}</span>
    </span>
  );
}

/* ----------------------------------------------------------------- spinner */

export function Spinner({ size = '2', className }: { size?: Size; className?: string }) {
  const px = { '1': 18, '2': 26, '3': 32, '4': 40 }[String(Math.min(Number(size), 4))] ?? 26;
  return (
    <span
      className={cx('gb-spinner', className)}
      style={{ width: px, height: px }}
      aria-hidden="true"
    />
  );
}
